import { AlertTriangle, GitBranch, HelpCircle, type LucideIcon } from 'lucide-react'
import type { IssueType } from '@/components/analysis/summary/types/triage'

export interface ImprovementTypeStyle {
	foreground: string
	background: string
	border: string
	accent: string
	selectedFill: string
	markerClass: string
	badgeClass: string
	spotTypeClass: string
	labelKey: string
	Icon: LucideIcon
	iconSvg: string
}

const STROKE_SVG =
	'xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"'

/** One hue per improvement type: dark foreground, light background, matching border/accent. */
export const IMPROVEMENT_TYPE_STYLES: Record<IssueType, ImprovementTypeStyle> = {
	exception: {
		foreground: '#92400E',
		background: '#FEF3C7',
		border: '#B45309',
		accent: '#F59E0B',
		selectedFill: '#FFFBEB',
		markerClass: 'improvement-exception',
		badgeClass: 'bpmn-improvement-badge--exception',
		spotTypeClass: 'mm-spot-type-exception',
		labelKey: 'pages.analysisDetail.structuredSummaryEditor.triage.typeException',
		Icon: GitBranch,
		iconSvg: `<svg ${STROKE_SVG}><line x1="6" x2="6" y1="3" y2="15"/><circle cx="18" cy="6" r="3"/><circle cx="6" cy="18" r="3"/><path d="M18 9a9 9 0 0 1-9 9"/></svg>`,
	},
	bottleneck: {
		foreground: '#9F1239',
		background: '#FFE4E6',
		border: '#BE123C',
		accent: '#E11D48',
		selectedFill: '#FFF1F2',
		markerClass: 'improvement-bottleneck',
		badgeClass: 'bpmn-improvement-badge--bottleneck',
		spotTypeClass: 'mm-spot-type-bottleneck',
		labelKey: 'pages.analysisDetail.structuredSummaryEditor.triage.typeBottleneck',
		Icon: AlertTriangle,
		iconSvg: `<svg ${STROKE_SVG}><path d="m21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3"/><path d="M12 9v4"/><path d="M12 17h.01"/></svg>`,
	},
	question: {
		foreground: '#1E40AF',
		background: '#DBEAFE',
		border: '#1D4ED8',
		accent: '#3B82F6',
		selectedFill: '#EFF6FF',
		markerClass: 'improvement-question',
		badgeClass: 'bpmn-improvement-badge--question',
		spotTypeClass: 'mm-spot-type-question',
		labelKey: 'pages.analysisDetail.structuredSummaryEditor.triage.typeQuestion',
		Icon: HelpCircle,
		iconSvg: `<svg ${STROKE_SVG}><circle cx="12" cy="12" r="10"/><path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"/><path d="M12 17h.01"/></svg>`,
	},
}

export const ADDRESSED_MARKER_CLASS = 'improvement-addressed'
export const SPOT_SELECTED_CLASS = 'mm-spot-selected'
export const SPOT_RELATED_CLASS = 'mm-spot-related'
export const SPOT_HOVERED_CLASS = 'mm-spot-hovered'
export const SPOT_KEEP_CLASS = 'mm-spot-keep'
export const SPOT_DIM_CLASS = 'mm-spot-dim'
export const SPOT_TYPE_CLASSES = [
	IMPROVEMENT_TYPE_STYLES.exception.spotTypeClass,
	IMPROVEMENT_TYPE_STYLES.bottleneck.spotTypeClass,
	IMPROVEMENT_TYPE_STYLES.question.spotTypeClass,
] as const

export const IMPROVEMENT_MARKER_CLASSES = [
	IMPROVEMENT_TYPE_STYLES.exception.markerClass,
	IMPROVEMENT_TYPE_STYLES.bottleneck.markerClass,
	IMPROVEMENT_TYPE_STYLES.question.markerClass,
	ADDRESSED_MARKER_CLASS,
] as const

export const SPOTLIGHT_MARKER_CLASSES = [
	SPOT_SELECTED_CLASS,
	SPOT_RELATED_CLASS,
	SPOT_HOVERED_CLASS,
	SPOT_KEEP_CLASS,
	...SPOT_TYPE_CLASSES,
] as const

export const ADDRESSED_CHECK_SVG = `<svg ${STROKE_SVG}><polyline points="20 6 9 17 4 12"/></svg>`

export function styleForType(type: IssueType | undefined): ImprovementTypeStyle {
	return IMPROVEMENT_TYPE_STYLES[type ?? 'question']
}
